/**
 * Utility functions for handling quantity values with 4-decimal precision
 * Required for DigiFact FEL compliance
 */

/**
 * Format quantity for display in UI (removes trailing zeros for readability)
 */
export function formatQuantityDisplay(value: number): string {
  if (isNaN(value) || !isFinite(value)) return '0';
  
  // For whole numbers, just show the number
  if (value % 1 === 0) {
    return value.toString();
  }
  
  // For decimals, show up to 4 decimal places but remove trailing zeros
  return parseFloat(value.toFixed(4)).toString();
}

/**
 * Format quantity specifically for DigiFact XML (always 4 decimals)
 */
export function formatQuantityForXML(value: number): string {
  if (isNaN(value) || !isFinite(value)) return '0.0000';
  return value.toFixed(4);
}

/**
 * Parse quantity from input, ensuring minimum value
 */
export function parseQuantityInput(value: string, minValue: number = 0.0001): number {
  const parsed = parseFloat(value);
  if (isNaN(parsed) || parsed < minValue) {
    return minValue;
  }
  // Round to 4 decimal places to avoid floating point issues
  return Math.round(parsed * 10000) / 10000;
}

/**
 * Increment quantity by small amounts (0.25 for better UX)
 */
export function incrementQuantity(currentValue: number, step: number = 0.25): number {
  const newValue = currentValue + step;
  return Math.round(newValue * 10000) / 10000;
}

/**
 * Decrement quantity by small amounts, ensuring minimum value
 */
export function decrementQuantity(currentValue: number, step: number = 0.25, minValue: number = 0.0001): number {
  const newValue = Math.max(minValue, currentValue - step);
  return Math.round(newValue * 10000) / 10000;
}

/**
 * Validate if quantity has valid precision (max 4 decimals)
 */
export function isValidQuantityPrecision(value: number): boolean {
  const str = value.toString();
  const decimalIndex = str.indexOf('.');
  if (decimalIndex === -1) return true;
  return str.substring(decimalIndex + 1).length <= 4;
}